import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { useCreateLead, useLeads } from '@/hooks/queries';
import { useDebounced } from '@/hooks/useDebounced';
import { IconCheck, IconPlus, IconSearch, IconX } from '@/components/Icons';
import { nowHHMM, todayISO } from '@/lib/format';
import type { Lead } from '@/lib/types';

/** Menos que isso casa com clientes demais para ajudar. */
const MIN_QUERY = 2;
const MAX_RESULTS = 8;

export interface PickedLead {
  id: string;
  name: string;
}

interface LeadPickerProps {
  /** Id do cliente escolhido; vazio = nenhum. */
  value: string;
  /** Nome do cliente escolhido, para mostrar sem buscar de novo. */
  valueName: string;
  onChange: (lead: PickedLead | null) => void;
  isAdmin: boolean;
  /** Cliente obrigatório (o gás é sempre de alguém). */
  required?: boolean;
  /**
   * Busca também os clientes da base, sem responsável — no comprovante: vincular
   * um deles passa o cliente para o membro ("puxar o lead da base").
   */
  fromBase?: boolean;
  /** Deixa cadastrar o cliente ali mesmo, quando ele não aparece na busca. */
  allowCreate?: boolean;
  /** Responsável do cliente cadastrado ali (o administrador lançando por alguém); padrão: quem cadastra. */
  newLeadOwnerId?: string;
}

interface NewLeadDraft {
  name: string;
  email: string;
  phone: string;
}

/** O que foi digitado na busca já preenche o campo certo do cadastro. */
function draftFromQuery(query: string): NewLeadDraft {
  const text = query.trim();
  if (text.includes('@')) return { name: '', email: text, phone: '' };
  if (/^[\d\s()+-]{6,}$/.test(text)) return { name: '', email: '', phone: text };
  return { name: text, email: '', phone: '' };
}

/** "e-mail · telefone · responsável" — o que ajuda a distinguir dois clientes de mesmo nome. */
function detailsOf(lead: Lead, isAdmin: boolean): string {
  const parts = [lead.email, lead.phone];
  if (isAdmin) parts.push(lead.ownerName ?? 'Sem responsável');
  // Para o membro, o da base é o que ainda não tem dono: vinculado, passa a ser dele.
  else if (lead.ownerId === null) parts.push('Da base — passa a ser seu');
  return parts.filter(Boolean).join(' · ');
}

/**
 * Escolha do cliente por busca (nome, e-mail ou telefone). Com centenas de
 * clientes vindos da Ephra, uma lista inteira num select não serve. A lista
 * de resultados abre logo abaixo do campo, e não por cima dele, para não ser
 * cortada dentro da janela do comprovante. Setas escolhem e Enter confirma.
 */
export function LeadPicker({
  value, valueName, onChange, isAdmin, required = false, fromBase = false, allowCreate = false, newLeadOwnerId,
}: LeadPickerProps) {
  const inputId = useId();
  const listId = useId();
  const hintId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [picked, setPicked] = useState<Lead | null>(null);
  const [newLead, setNewLead] = useState<NewLeadDraft | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const createLead = useCreateLead();

  const search = useDebounced(query.trim());
  const searching = search.length >= MIN_QUERY;
  const { data, isFetching, isPlaceholderData } = useLeads(
    { search, pageSize: MAX_RESULTS, sort: 'name', ...(fromBase ? { includeUnowned: 'true' } : {}) },
    { enabled: searching },
  );
  // Enquanto a busca do que está digitado não volta, a lista fica vazia ("Buscando…"): o
  // resultado da busca anterior não aparece nem pode ser escolhido com um Enter apressado.
  const settled = searching && search === query.trim() && !isPlaceholderData;
  const results = settled ? data?.items ?? [] : [];
  const showList = open && query.trim().length >= MIN_QUERY;

  function choose(lead: Lead) {
    setPicked(lead);
    setQuery('');
    setOpen(false);
    onChange({ id: lead.id, name: lead.name });
  }

  function startCreate() {
    setNewLead(draftFromQuery(query));
    setCreateError(null);
    setOpen(false);
  }

  async function saveNewLead() {
    if (!newLead) return;
    if (newLead.name.trim().length < 2) return setCreateError('Informe o nome do cliente (ao menos 2 letras).');
    setCreateError(null);
    try {
      const { lead } = await createLead.mutateAsync({
        name: newLead.name.trim(),
        email: newLead.email.trim() || undefined,
        phone: newLead.phone.trim() || undefined,
        date: todayISO(),
        time: nowHHMM(),
        ...(newLeadOwnerId ? { ownerId: newLeadOwnerId } : {}),
      });
      setNewLead(null);
      choose(lead);
    } catch (caught) {
      setCreateError(caught instanceof Error ? caught.message : 'Não deu para cadastrar o cliente.');
    }
  }

  function clear() {
    setPicked(null);
    onChange(null);
    // Depois de limpar, o campo de busca aparece: o foco vai para ele.
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      if (results.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => (current + step + results.length) % results.length);
    } else if (event.key === 'Enter') {
      // Enter aqui escolhe o cliente; nunca envia o formulário do comprovante.
      event.preventDefault();
      const lead = results[active];
      if (showList && lead) choose(lead);
    } else if (event.key === 'Escape' && showList) {
      event.stopPropagation();
      setOpen(false);
    }
  }

  if (value) {
    const details = picked && picked.id === value ? detailsOf(picked, isAdmin) : '';
    return (
      <div className="field">
        <span className="field-label">{required ? 'Cliente *' : 'Cliente'}</span>
        <div className="lead-picked">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="truncate" style={{ fontWeight: 500 }}>{valueName || picked?.name || 'Cliente vinculado'}</div>
            {details ? <div className="lead-picker-sub truncate">{details}</div> : null}
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={clear} aria-label="Tirar o cliente vinculado">
            <IconX /> Trocar
          </button>
        </div>
      </div>
    );
  }

  if (newLead) {
    const onEnter = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      void saveNewLead();
    };
    return (
      <div className="field lead-picker-new" role="group" aria-labelledby={`${inputId}-novo`}>
        <span id={`${inputId}-novo`} className="field-label">{required ? 'Cliente * — cadastrar novo' : 'Cliente — cadastrar novo'}</span>
        <div className="lead-picker-new-grid">
          <input
            className="inp"
            aria-label="Nome do cliente"
            placeholder="Nome *"
            value={newLead.name}
            onChange={(event) => setNewLead({ ...newLead, name: event.target.value })}
            onKeyDown={onEnter}
            maxLength={140}
            autoFocus
          />
          <input
            className="inp"
            aria-label="E-mail do cliente"
            placeholder="E-mail"
            type="email"
            value={newLead.email}
            onChange={(event) => setNewLead({ ...newLead, email: event.target.value })}
            onKeyDown={onEnter}
          />
          <input
            className="inp"
            aria-label="Telefone do cliente"
            placeholder="Telefone"
            value={newLead.phone}
            onChange={(event) => setNewLead({ ...newLead, phone: event.target.value })}
            onKeyDown={onEnter}
            maxLength={40}
          />
        </div>
        {createError ? <span className="field-error">{createError}</span> : (
          <span className="field-hint">O cliente fica cadastrado {isAdmin ? 'para o membro do comprovante' : 'no seu nome'} e já vinculado aqui.</span>
        )}
        <div className="lead-picker-new-actions">
          <button type="button" className="btn btn-sm btn-primary" disabled={createLead.isPending} onClick={() => void saveNewLead()}>
            {createLead.isPending ? <span className="spinner" /> : <IconCheck />} Cadastrar e vincular
          </button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setNewLead(null)}>Voltar à busca</button>
        </div>
      </div>
    );
  }

  return (
    <div className="field">
      <label className="field-label" htmlFor={inputId}>{required ? 'Cliente * — buscar' : 'Cliente — buscar'}</label>
      <div className="search-input">
        <span className="search-input-icon" aria-hidden="true"><IconSearch /></span>
        <input
          ref={inputRef}
          id={inputId}
          className="inp"
          type="search"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-describedby={hintId}
          aria-activedescendant={showList && results[active] ? `${listId}-${active}` : undefined}
          autoComplete="off"
          placeholder="Digite nome, e-mail ou telefone"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
        />
      </div>
      <span id={hintId} className="field-hint">
        {required ? 'Obrigatório.' : 'Opcional.'} Busque pelo nome, e-mail ou telefone — ao menos {MIN_QUERY} letras
        {fromBase && !isAdmin ? ', nos seus clientes e na base.' : '.'}
        {allowCreate ? (
          <>
            {' '}
            <button type="button" className="link-btn lead-picker-create-link" onClick={startCreate}>Cadastrar cliente novo</button>
          </>
        ) : null}
      </span>

      {showList ? (
        <ul id={listId} role="listbox" className="lead-picker-results" aria-label="Clientes encontrados">
          {results.map((lead, index) => (
            <li
              key={lead.id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              className="lead-picker-option"
              // mousedown, e não click: o clique viria depois do blur, que já fechou a lista.
              onMouseDown={(event) => {
                event.preventDefault();
                choose(lead);
              }}
              onMouseEnter={() => setActive(index)}
            >
              <span className="truncate" style={{ fontWeight: 500 }}>{lead.name}</span>
              <span className="lead-picker-sub truncate">{detailsOf(lead, isAdmin) || 'Sem e-mail nem telefone'}</span>
            </li>
          ))}
          {results.length === 0 ? (
            <li className="lead-picker-empty" role="presentation">
              {!settled || isFetching ? 'Buscando…' : `Nenhum cliente encontrado para “${query.trim()}”.`}
            </li>
          ) : null}
          {allowCreate && settled && !isFetching ? (
            <li
              className="lead-picker-option lead-picker-create"
              role="presentation"
              // mousedown, como nas opções: o clique viria depois do blur, que fecha a lista.
              onMouseDown={(event) => {
                event.preventDefault();
                startCreate();
              }}
            >
              <span><IconPlus /> Não achou? Cadastrar cliente novo</span>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
